import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {TriggerConfiguration} from '@models/adminstration/machine/trigger/trigger-configuration';
import {TriggerConfigurationService} from '@services/administration/machine/trigger/trigger-configuration.service';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {Machine} from '@models/machine/machine';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {TRIGGER_TYPE} from '@constants/administration/machine/trigger-type';
import {ExpressionSignal} from '@models/adminstration/machine/trigger/expression-signal';
import {TriggerStylesService} from '@services/styles/administration/machine/trigger/trigger-styles.service';
import {UtilsService} from '@services/utils/utils.service';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-trigger-configuration-edit',
    templateUrl: './trigger-configuration-edit.component.html',
    styleUrls: ['./trigger-configuration-edit.component.scss'],
    standalone: false
})
export class TriggerConfigurationEditComponent implements OnInit {

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  machine: Machine = new Machine();

  @Input()
  set _triggerConfiguration(value) {
    this.triggerConfiguration = new TriggerConfiguration(value);
  };

  triggerConfiguration: TriggerConfiguration = new TriggerConfiguration();
  isAdminOrHigher = false;
  submitted: boolean = false;
  triggerTypes: string[] = [];
  triggerType = TRIGGER_TYPE;
  selectedTriggerType: TRIGGER_TYPE = TRIGGER_TYPE.UNKNOWN;
  timeBasedFormIsValid = false;
  valueBasedFormIsValid = false;
  opcOfflineFormIsValid = false;
  opcNoDataFormIsValid = false;
  triggerConfigurationForm: UntypedFormGroup = new UntypedFormGroup({});
  addTriggerConfigurationRequest: any = {};
  triggerQueriesList: TriggerQuery[] = [];
  isTriggerActive = false;
  isLoadingTriggerQueries = false;
  cloudPlugs: Gateway[] = [];
  isLoadingCloudPlugs = false;
  signals: ExpressionSignal[] = [];

  private COMPONENT_NAME = 'TriggerConfigurationEditComponent';
  private GATEWAY_NOT_DEFINED_FOR_TRIGGER_CONFIG = 'GATEWAY_NOT_DEFINED_FOR_TRIGGER_CONFIG';
  private ERROR_SAVING_TRIGGER_CONFIGURATION = 'ERROR_SAVING_TRIGGER_CONFIGURATION';

  constructor(
      private activeModal: NgbActiveModal,
      private loadingIndicator: LoadingIndicatorService,
      private translateService: TranslateService,
      private notificationService: NotificationService,
      private triggerConfigurationService: TriggerConfigurationService,
      private modalService: ModalService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private triggerStyleService: TriggerStylesService,
      private utilsService: UtilsService) {
  }

  get f() {
    return this.triggerConfigurationForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return this.submitted ? !this.formsAreValid() : this.submitted && this.triggerConfigurationForm.invalid;
  };

  ngOnInit(): void {
    this.isAdminOrHigher = this.utilsService.isAdminOrHigher(this.userRole.isAdminRole, this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole);
    this.updateResolveFlags();
    this.setTriggerTypes();
    this.setAvailableTriggerQueries();
    this.setIsTriggerActive();
    this.setCloudPlugs();
    this.generateForm();
    this.onTriggerTypeChange(this.triggerConfiguration.triggerType);
    this.onLoadSignalsForCloudPlug(this.triggerConfiguration.cloudplugId || '');
    console.log(this.triggerConfiguration);
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onTriggerActionSelected(selectedTriggerQuery: TriggerQuery) {
    if (!this.triggerConfiguration.triggerQueries) {
      this.triggerConfiguration.triggerQueries = [];
    }
    this.triggerConfiguration.triggerQueries.push(selectedTriggerQuery);
    this.triggerQueriesList = this.triggerQueriesList.filter(triggerQuery => triggerQuery.id !== selectedTriggerQuery.id);
    this.triggerConfigurationForm.controls.triggerAction.setValue('');
  }

  onRemoveTriggerQuery(selectedTriggerQuery: TriggerQuery): void {
    this.triggerConfiguration.triggerQueries = this.triggerConfiguration.triggerQueries.filter(triggerQuery => triggerQuery.id !== selectedTriggerQuery.id);
    this.triggerQueriesList.push(selectedTriggerQuery);
    this.triggerQueriesList = [...this.triggerQueriesList];
  }

  triggerActionRemoveButtonDisabled(triggerActionType: string): boolean {
    return this.triggerConfigurationService.triggerActionRemoveButtonDisabled(this.userRole, triggerActionType);
  }

  onResolveEmailCheckBoxChange(triggerQuery: TriggerQuery) {
    this.triggerConfigurationService.resolveEmailCheckBoxChange(triggerQuery, this.triggerConfiguration);
  }

  showResolveCheckbox(triggerQuery: TriggerQuery) {
    return this.triggerConfigurationService.showResolveCheckbox(triggerQuery, this.triggerConfiguration, this.selectedTriggerType);
  }

  onLoadSignalsForCloudPlug(gatewayId: string): void {
    this.triggerConfiguration.cloudplugId = gatewayId;
    if (this.triggerConfigurationService.isSignalBasedTrigger(this.selectedTriggerType)) {
      this.triggerConfigurationService.getSignalsLoadingSubject().next(true);
      this.triggerConfigurationService
      .getTriggerExpressionSignals(gatewayId)
      .subscribe(
          {
            next: (expressionSignals: ExpressionSignal[]) => {
              if (expressionSignals?.length > 0) {
                expressionSignals = expressionSignals.filter(m => !m.triggerSignal);
                this.signals = this.tableHeaderSortableService.sortItems<ExpressionSignal>(expressionSignals, 'signalLabel', 'asc');
                this.triggerConfigurationService.setExpressionsLabelsAndUnits(this.triggerConfiguration.triggerExpressions, this.signals);
              } else {
                this.signals = [];
              }
              this.triggerConfigurationService.getSignalsLoadingSubject().next(false);
            },
            error: error => {
              this.notificationService.error('TriggerConfigModal. Error getting signal list.', error, this.COMPONENT_NAME, true);
              this.triggerConfigurationService.getSignalsLoadingSubject().next(false);
            }
          });
    }
  }

  onTimeBasedFormIsValid(timeBasedFormIsValid: boolean) {
    this.timeBasedFormIsValid = timeBasedFormIsValid;
  }

  onValueBasedFormIsValid(valueBasedFormIsValid: boolean) {
    this.valueBasedFormIsValid = valueBasedFormIsValid;
  }

  onOpcOfflineFormIsValid(opcOfflineFormIsValid: boolean) {
    this.opcOfflineFormIsValid = opcOfflineFormIsValid;
  }

  onOpcNoDataFormIsValid(opcNoDataFormIsValid: boolean) {
    this.opcNoDataFormIsValid = opcNoDataFormIsValid;
  }

  onUpdatedTimeBased(updatedTriggerConfiguration: TriggerConfiguration) {
    this.triggerConfiguration.triggerInterval = updatedTriggerConfiguration.triggerInterval;
    this.triggerConfiguration.triggerIntervalType = updatedTriggerConfiguration.triggerIntervalType;
    this.triggerConfiguration.effectiveStartDate = updatedTriggerConfiguration.effectiveStartDate;
    this.triggerConfiguration.cloudplugId = updatedTriggerConfiguration.cloudplugId;
    this.updateTriggerConfigurationProperties();
  }

  onUpdatedValueBased(updatedTriggerConfiguration: TriggerConfiguration) {
    this.triggerConfiguration.cloudplugId = updatedTriggerConfiguration.cloudplugId;
    this.triggerConfiguration.signalEdge = updatedTriggerConfiguration.signalEdge;
    this.triggerConfiguration.timeDependencyConfig = updatedTriggerConfiguration.timeDependencyConfig;
    if (!this.triggerConfiguration.signalEdge || this.triggerConfiguration.signalEdge == 'BOTH') {
      this.triggerConfiguration.resolveActions = [];
      console.log('reset');
    }
  }

  onUpdatedOpcOffline(updatedTriggerConfiguration: TriggerConfiguration) {
    this.triggerConfiguration.triggerInterval = updatedTriggerConfiguration.triggerInterval;
    this.triggerConfiguration.triggerIntervalType = updatedTriggerConfiguration.triggerIntervalType;
    this.triggerConfiguration.cloudplugId = updatedTriggerConfiguration.cloudplugId;
    this.updateTriggerConfigurationProperties();
  }

  onUpdatedOpcNoData(updatedTriggerConfiguration: TriggerConfiguration) {
    this.triggerConfiguration.signalEdge = updatedTriggerConfiguration.signalEdge;
    this.triggerConfiguration.cloudplugId = updatedTriggerConfiguration.cloudplugId;
    this.updateTriggerConfigurationProperties();
  }

  onTriggerTypeChange(triggerType: TRIGGER_TYPE): void {
    this.selectedTriggerType = triggerType;
    this.timeBasedFormIsValid = true;
    this.valueBasedFormIsValid = true;
    this.opcOfflineFormIsValid = true;
    this.opcNoDataFormIsValid = true;
    switch (triggerType) {
      case TRIGGER_TYPE.REAL_TIME_BASED:
        this.timeBasedFormIsValid = false;
        break;
      case TRIGGER_TYPE.VALUE_BASED:
        this.valueBasedFormIsValid = false;
        break;
      case TRIGGER_TYPE.OPC_OFFLINE:
        this.opcOfflineFormIsValid = false;
        break;
      case TRIGGER_TYPE.OPC_NO_DATA:
        this.opcNoDataFormIsValid = false;
        break;
    }
  }

  onSave(): void {
    this.submitted = true;

    if (!this.formsAreValid()) {
      return;
    }

    if (this.triggerConfiguration.id === -1) {
      this.addTriggerConfiguration();
    } else {
      this.updateTriggerConfiguration();
    }

  }

  showResolveEmailField(triggerExecutionInDataflow: boolean | undefined, selectedTriggerType: string) {
    return this.triggerConfigurationService.showResolveEmailField(triggerExecutionInDataflow, selectedTriggerType);
  }

  showTimeBasedComponent() {
    return this.triggerConfigurationService.showTimeBasedComponent(this.isAdminOrHigher, this.selectedTriggerType);
  }

  showValueBasedComponent() {
    return this.triggerConfigurationService.showValueBasedComponent(this.selectedTriggerType);
  }

  showOpcOfflineComponent() {
    return this.triggerConfigurationService.showOpcOfflineComponent(this.isAdminOrHigher, this.selectedTriggerType);
  }

  showOpcNoDataComponent() {
    return this.triggerConfigurationService.showOpcNoDataComponent(this.isAdminOrHigher, this.selectedTriggerType);
  }

  getStatusCssClass(statusSuccess: boolean) {
    return this.triggerStyleService.getTriggerConfigurationStatusClass(statusSuccess);
  }

  getModal(title: string, body: string): NgbModalRef {
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    this.setModalInputParameters(modalRef, title, body);
    return modalRef;
  }

  public formsAreValid(): boolean {
    return this.triggerConfigurationForm.valid && this.nestedFormIsValid();
  }

  private nestedFormIsValid(): boolean {
    if (this.userRole.isDistributorRole) {
      return true;
    }
    switch (this.selectedTriggerType) {
      case TRIGGER_TYPE.REAL_TIME_BASED:
        return this.timeBasedFormIsValid;
      case TRIGGER_TYPE.VALUE_BASED:
        return this.valueBasedFormIsValid;
      case TRIGGER_TYPE.OPC_OFFLINE:
        return this.opcOfflineFormIsValid;
      case TRIGGER_TYPE.OPC_NO_DATA:
        return this.opcNoDataFormIsValid;
    }
    return false;
  }

  private updateResolveFlags() {
    if (this.triggerConfiguration.triggerQueries && this.triggerConfiguration.resolveActions?.length > 0) {
      this.triggerConfiguration.triggerQueries.forEach(triggerQuery => {
        if (!triggerQuery.hasOwnProperty('isResolveEvent')) {
          triggerQuery.isResolveEvent = false;
        }
        if (this.triggerConfiguration.resolveActions.indexOf(triggerQuery.id) > -1) {
          triggerQuery.isResolveEvent = true;
        }
      });
    }
  }

  private setTriggerTypes(): void {
    Object.values(this.triggerType).forEach((v: string) => {
      if (v !== TRIGGER_TYPE.EVENT_SEVERITY_BASED && v !== TRIGGER_TYPE.UNKNOWN) {
        this.triggerTypes.push(v);
      }
    });
  }

  private generateForm(): void {
    this.triggerConfigurationForm = new UntypedFormGroup({
      name: new UntypedFormControl({
        value: this.triggerConfiguration.name,
        disabled: !this.isAdminOrHigher
      }, [Validators.required]),
      triggerType: new UntypedFormControl({
        value: this.triggerConfiguration.triggerType,
        disabled: !this.isAdminOrHigher
      }, [Validators.required]),
      triggerAction: new UntypedFormControl('')
    });
  }

  private setAvailableTriggerQueries(): void {
    if (this.machine.id) {
      this.isLoadingTriggerQueries = true;
      this.triggerConfigurationService.getAvailableTriggerQueries(this.machine.id).subscribe(
          {
            next: (result: TriggerQuery[]) => {
              if (result?.length > 0) {
                result.forEach((triggerQuery: TriggerQuery) => {
                  if (!triggerQuery.isResolveEvent) {
                    triggerQuery.isResolveEvent = false;
                  }
                });
              }
              this.triggerQueriesList = this.triggerConfigurationService.availableTriggerQueries(this.userRole, result);
              this.isLoadingTriggerQueries = false;
            }, error: error => {
              this.isLoadingTriggerQueries = false;
              this.notificationService.error('Error loading trigger actions', error, this.COMPONENT_NAME, true);
            }
          });
    }
  }

  private setIsTriggerActive(): void {
    if (this.machine.id === -1 || !this.machine.id) {
      this.isTriggerActive = false;
    } else {
      this.triggerConfigurationService.triggerIsActive(this.machine.id, this.triggerConfiguration.id).subscribe(
          {
            next: (result: boolean) => {
              this.isTriggerActive = result;
            },
            error: error => {
              console.log(error);
            }
          });
    }
  }

  private setCloudPlugs(): void {
    if (this.machine.id) {
      this.isLoadingCloudPlugs = true;
      this.triggerConfigurationService.getCloudPlugs(this.machine.id).subscribe({
        next: (cloudPlugs: Gateway[]) => {
          this.cloudPlugs = this.tableHeaderSortableService.sortItems<Gateway>(cloudPlugs, 'name', 'asc');
          this.isLoadingCloudPlugs = false;
        }, error: error => {
          this.notificationService.error('Error loading gateways for machine', error, this.COMPONENT_NAME, true);
          this.isLoadingCloudPlugs = false;
        }
      });
    }
  }

  private addTriggerConfiguration(): void {
    if (this.machine.id) {
      this.loadingIndicator.show(this.translateService.instant('Loading'));
      this.setAddTriggerConfigurationRequest();
      this.triggerConfigurationService.addTriggerConfiguration(this.machine.id, this.addTriggerConfigurationRequest)
      .subscribe({
        next: (addedTriggerConfiguration: TriggerConfiguration) => {
          this.activeModal.close(addedTriggerConfiguration);
          const message = this.translateService.instant('Successfully created/updated trigger configuration');
          this.notificationService.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error: error => {
          this.handleError(error);
        }
      });
    }
  }

  private updateTriggerConfiguration(): void {
    if (this.machine.id) {
      this.loadingIndicator.show(this.translateService.instant('Loading'));
      this.setTriggerConfigurationProperties();
      this.triggerConfigurationService.updateTriggerConfiguration(this.machine.id, this.triggerConfiguration)
      .subscribe({
        next: (addedTriggerConfiguration: TriggerConfiguration) => {
          this.activeModal.close(addedTriggerConfiguration);
          const message = this.translateService.instant('Successfully created/updated trigger configuration');
          this.notificationService.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error: error => {
          this.handleError(error);
        }
      });
    }
  }

  private handleError(error): void {
    const errorReasons = error.data;
    if (errorReasons?.length > 0) {
      const errorReason = errorReasons[0];
      if (errorReason === this.GATEWAY_NOT_DEFINED_FOR_TRIGGER_CONFIG) {
        const message = this.translateService.instant('Gateway missing for streaming based trigger');
        this.notificationService.error(message, error, this.COMPONENT_NAME, true);
      } else if (errorReason === this.ERROR_SAVING_TRIGGER_CONFIGURATION) {
        const message = this.translateService.instant('Could not save trigger configuration. Gap detection in progress');
        this.notificationService.error(message, error, this.COMPONENT_NAME, true);
      } else {
        this.showGenericSaveErrorMessage(error);
      }
    } else {
      this.showGenericSaveErrorMessage(error);
    }
    this.loadingIndicator.hide();
  }

  private showGenericSaveErrorMessage(error) {
    const message = this.translateService.instant('Error creating/updating trigger action');
    this.notificationService.error(message, error, this.COMPONENT_NAME, true);
  }

  private setAddTriggerConfigurationRequest(): void {
    this.addTriggerConfigurationRequest.cloudplugId = this.triggerConfiguration.cloudplugId;
    if (!this.triggerConfiguration.effectiveStartDate) {
      this.triggerConfiguration.effectiveStartDate = new Date().toISOString();
    }
    this.addTriggerConfigurationRequest.effectiveStartDate = this.triggerConfiguration.effectiveStartDate;
    this.addTriggerConfigurationRequest.id = this.triggerConfiguration.id;
    this.addTriggerConfigurationRequest.name = this.triggerConfigurationForm.controls.name.value;
    this.addTriggerConfigurationRequest.timeDependencyConfig = this.triggerConfiguration.timeDependencyConfig;
    this.addTriggerConfigurationRequest.timestamp = Date.now();
    this.addTriggerConfigurationRequest.triggerExpressions = this.triggerConfigurationService.setTriggerExpressionsRequest(this.triggerConfiguration.triggerExpressions);
    this.addTriggerConfigurationRequest.triggerQueries = [];
    this.triggerConfiguration.triggerQueries.forEach((triggerQuery: TriggerQuery) => {
      this.addTriggerConfigurationRequest.triggerQueries.push(triggerQuery.id);
    });
    this.addTriggerConfigurationRequest.triggerType = this.selectedTriggerType;

    this.triggerConfigurationService.setResolveActionsRequest(this.selectedTriggerType, this.triggerConfiguration, this.addTriggerConfigurationRequest);
    this.triggerConfigurationService.setRequestForSetParameter(this.triggerConfiguration, this.addTriggerConfigurationRequest);
  }

  private setTriggerConfigurationProperties(): void {
    this.triggerConfiguration.triggerQueryIds = [];
    if (this.triggerConfiguration.triggerQueries) {
      this.triggerConfiguration.triggerQueries.forEach((triggerQuery: TriggerQuery) => {
        this.triggerConfiguration.triggerQueryIds.push(triggerQuery.id);
      });
    }
    this.triggerConfiguration.triggerQueries = [];
    this.triggerConfiguration.triggerQueries = this.triggerConfiguration.triggerQueryIds;
    this.triggerConfiguration.name = this.triggerConfigurationForm.controls.name.value;
    this.triggerConfiguration.triggerType = this.selectedTriggerType;
  }

  private updateTriggerConfigurationProperties(): void {
    this.triggerConfiguration.resolveActions = [];
  }

  private setModalInputParameters(modalRef: NgbModalRef, title: string, body: string) {
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.body = body;
    modalRef.componentInstance.cancelButtonText = null;
    modalRef.componentInstance.okButtonText = this.translateService.instant('Close');
  }
}
